import { apiBaseUrl } from '../../src/services/api';
import { t, uiLanguage } from '../../src/services/language';
import type { EmailAuthResult } from '../../src/shared/types';
import { SUPPORT_EMAIL } from './shims/support';
import { getSupabase, isSupabaseConfigured } from './shims/supabaseClient';

/**
 * E-posta + şifreyle giriş ve kayıt (web). Kayıtta hesap doğrulanmamış açılır,
 * adrese 6 haneli kod gider (sunucu, Resend); kod girilmeden giriş yapılamaz —
 * Supabase'de "Confirm email" açık, doğrulanmamış hesabın şifreyle girişini reddediyor.
 *
 * Kod doğrulanınca sunucu tek kullanımlık bir oturum anahtarı döndürüyor
 * (`verifyOtp` ile oturuma çevrilir). Şifremi unuttum da aynı kodla çalışıyor.
 * Uçlar: web/app/api/v1/auth/email/*.
 */

const OK: EmailAuthResult = { ok: true };

function fail(error: string, reason?: string, retryAfterSec?: number): EmailAuthResult {
  return { ok: false, error, ...(reason ? { reason } : {}), ...(retryAfterSec ? { retryAfterSec } : {}) };
}

/** Sunucu mesajları Türkçe; İngilizce arayüzde bilinen durumlar burada çevriliyor. */
function describe(reason: string | undefined, serverMessage: string): string {
  switch (reason) {
    case 'exists':
      return t(
        'Bu e-postayla zaten bir hesap var. Giriş yap ya da şifreni sıfırla.',
        'An account with this email already exists. Sign in or reset your password.'
      );
    case 'weak_password':
      return t(
        'Şifre en az 8 karakter olmalı; büyük harf, küçük harf ve rakam içermeli.',
        'The password must be at least 8 characters with an uppercase letter, a lowercase letter and a number.'
      );
    case 'invalid_email':
      return t('Geçerli bir e-posta adresi yaz.', 'Enter a valid email address.');
    case 'wrong':
      return t('Kod hatalı. Tekrar dene.', 'That code is incorrect. Try again.');
    case 'locked':
      return t('Çok fazla hatalı deneme. Yeni kod iste.', 'Too many incorrect attempts. Request a new code.');
    case 'expired':
      return t('Kodun süresi doldu. Yeni kod iste.', 'The code has expired. Request a new code.');
    case 'missing':
      return t('Geçerli bir kod yok. Yeni kod iste.', 'There is no valid code. Request a new code.');
    case 'no_account':
      return t('Bu e-postayla kayıtlı bir hesap yok. Kayıt olabilirsin.', 'There is no account with this email. You can sign up.');
    case 'banned':
      return t(
        `Hesabın askıya alındı. Ayrıntı için ${SUPPORT_EMAIL} adresine yazabilirsin.`,
        `Your account has been suspended. For details, write to ${SUPPORT_EMAIL}.`
      );
    case 'beta_invite_required':
      return t(
        `TalkNote şu an kapalı beta aşamasında ve bu e-posta davet listesinde değil. Davet için ${SUPPORT_EMAIL} adresine yazabilirsiniz.`,
        `TalkNote is in closed beta and this email is not on the invite list. To request an invite, write to ${SUPPORT_EMAIL}.`
      );
    default:
      return serverMessage || t('Bir sorun oluştu. Tekrar dene.', 'Something went wrong. Please try again.');
  }
}

/** Oturumsuz uç çağrısı; hata `EmailAuthResult` olarak döner, fırlatmaz. */
async function post<T extends object>(path: string, body: unknown): Promise<{ ok: true; data: T } | { ok: false; result: EmailAuthResult }> {
  if (!apiBaseUrl() || !isSupabaseConfigured()) {
    return { ok: false, result: fail(t('Uygulama yapılandırılmamış.', 'The app is not configured.')) };
  }
  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(20_000),
    });
  } catch {
    return { ok: false, result: fail(t('Sunucuya ulaşılamadı. Bağlantını kontrol et.', 'Could not reach the server. Check your connection.')) };
  }
  const json = (await res.json().catch(() => ({}))) as T & { error?: string; reason?: string; retryAfterSec?: number };
  if (res.ok) return { ok: true, data: json };
  const reason = json.reason ?? (res.status === 429 ? 'rate_limited' : undefined);
  return { ok: false, result: fail(describe(json.reason, json.error ?? ''), reason, json.retryAfterSec) };
}

/** Sunucunun verdiği tek kullanımlık anahtarla oturum açar. */
async function openSession(tokenHash: string): Promise<EmailAuthResult> {
  const { error } = await getSupabase().auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
  if (error) {
    console.error('[auth] e-posta oturumu açılamadı:', error.message);
    return fail(t('Doğrulandı ama oturum açılamadı. E-posta ve şifrenle giriş yap.', 'Verified, but the session could not be opened. Sign in with your email and password.'));
  }
  return OK;
}

export async function signInWithEmail(email: string, password: string): Promise<EmailAuthResult> {
  if (!isSupabaseConfigured()) return fail(t('Uygulama yapılandırılmamış (Supabase).', 'The app is not configured (Supabase).'));
  const { error } = await getSupabase().auth.signInWithPassword({ email: email.trim(), password });
  if (!error) return OK;
  const code = (error as { code?: string }).code ?? '';
  if (code === 'email_not_confirmed' || /not confirmed/i.test(error.message)) {
    // Kod ekranına geçilir; yeni kod hemen gönderilsin (öncekinin süresi dolmuş olabilir).
    const sent = await resendSignupCode(email);
    return fail(
      t('E-posta adresin henüz doğrulanmadı. Adresine gönderdiğimiz kodu gir.', 'Your email is not verified yet. Enter the code we sent you.'),
      'email_not_confirmed',
      sent.ok ? undefined : sent.retryAfterSec
    );
  }
  if (/banned/i.test(error.message)) return fail(describe('banned', ''), 'banned');
  if (code === 'over_request_rate_limit' || error.status === 429) {
    return fail(t('Çok fazla deneme yapıldı. Biraz sonra tekrar dene.', 'Too many attempts. Please try again later.'), 'rate_limited');
  }
  if (code === 'invalid_credentials' || error.status === 400) {
    return fail(
      t(
        'E-posta veya şifre hatalı. Google ile kaydolduysan Google ile giriş yap ya da "Şifremi unuttum" ile şifre belirle.',
        'Incorrect email or password. If you signed up with Google, sign in with Google or set a password with "Forgot password".'
      ),
      'invalid_credentials'
    );
  }
  console.error('[auth] e-postayla giriş başarısız:', error.message);
  return fail(t('Giriş yapılamadı. Tekrar dene.', 'Could not sign in. Please try again.'));
}

export async function signUpWithEmail(name: string, email: string, password: string): Promise<EmailAuthResult> {
  const res = await post('/api/v1/auth/email/signup', { name: name.trim(), email: email.trim(), password, language: uiLanguage() });
  return res.ok ? OK : res.result;
}

export async function verifySignupCode(email: string, code: string): Promise<EmailAuthResult> {
  const res = await post<{ tokenHash?: string }>('/api/v1/auth/email/verify', { email: email.trim(), code: code.trim() });
  if (!res.ok) return res.result;
  return res.data.tokenHash ? openSession(res.data.tokenHash) : fail(t('Doğrulama tamamlanamadı.', 'Verification could not be completed.'));
}

export async function resendSignupCode(email: string): Promise<EmailAuthResult> {
  const res = await post('/api/v1/auth/email/resend', { email: email.trim(), language: uiLanguage() });
  return res.ok ? OK : res.result;
}

export async function requestPasswordReset(email: string): Promise<EmailAuthResult> {
  const res = await post('/api/v1/auth/email/forgot', { email: email.trim(), language: uiLanguage() });
  return res.ok ? OK : res.result;
}

export async function resetPassword(email: string, code: string, password: string): Promise<EmailAuthResult> {
  const res = await post<{ tokenHash?: string }>('/api/v1/auth/email/reset', { email: email.trim(), code: code.trim(), password });
  if (!res.ok) return res.result;
  return res.data.tokenHash ? openSession(res.data.tokenHash) : fail(t('Şifre değiştirilemedi.', 'Could not change the password.'));
}
